import { State } from "xstate";
import { ParentState } from "@capone/checkout";
import { BookingQuestionId, PickupLocationType } from "redmond";

import { ActivityContext } from "../../../modules/book/state/types";

type ActivityStateType = State<ActivityContext>;
type ActivityStateWithoutValue = Pick<ActivityStateType, "context">;

export const getHasAdditionalDetailsStep = ({
  context,
}: ActivityStateWithoutValue) =>
  context[ParentState.activityAdditionalDetails].hasAdditionalDetailsStep;

export const getPickupLocations = ({ context }: ActivityStateWithoutValue) =>
  context.activityShop?.bookingQuestions.pickupLocations;

export const getIsPickupIncluded = ({ context }: ActivityStateWithoutValue) => {
  if (!context.activityShop) return false;

  const selectedBookableItem = context.activityShop?.bookableItems.find(
    (item) =>
      item.experienceOptionId.value === context.priceBreakdown?.optionId?.value
  );

  return selectedBookableItem?.isPickupIncluded;
};

export const getHasPickupLocationField = ({
  context,
}: ActivityStateWithoutValue) =>
  context[ParentState.activityAdditionalDetails].requiredBookingQuestions.some(
    (bookingQuestion) => bookingQuestion.id === BookingQuestionId.PickupPoint
  );

export const getBookableLangGuides = ({
  context,
}: ActivityStateWithoutValue) => {
  if (!context.activityShop) return [];

  const selectedBookableItem = context.activityShop?.bookableItems.find(
    (item) =>
      item.experienceOptionId.value === context.priceBreakdown?.optionId?.value
  );

  return selectedBookableItem?.languageGuides || [];
};

export const getHasSpecialRequirementField = ({
  context,
}: ActivityStateWithoutValue) =>
  context[ParentState.activityAdditionalDetails].requiredBookingQuestions.some(
    (bookingQuestion) =>
      bookingQuestion.id === BookingQuestionId.SpecialRequirements
  );

export const getPickupPointStates = ({
  context,
}: ActivityStateWithoutValue) => {
  const {
    selectedPickupPoint,
    hotelSelection,
    hotelName,
    address,
    city,
    zipCode,
  } = context[ParentState.activityAdditionalDetails];

  return {
    selectedPickupPoint,
    hotelSelection,
    hotelName,
    address,
    city,
    zipCode,
  };
};

export const getSelectedPickupPoint = ({
  context,
}: ActivityStateWithoutValue) =>
  context[ParentState.activityAdditionalDetails].selectedPickupPoint;

export const getSelectedLangGuide = ({ context }: ActivityStateWithoutValue) =>
  context[ParentState.activityAdditionalDetails].selectedLangGuide;

export const getSpecialRequirement = ({ context }: ActivityStateWithoutValue) =>
  context[ParentState.activityAdditionalDetails].specialRequirement;

export const getHasCompletedAdditionalDetailsStep = ({
  context,
}: ActivityStateWithoutValue) => {
  const selectedPickupPoint = getSelectedPickupPoint({ context });
  const selectedLangGuide = getSelectedLangGuide({ context });
  const specialRequirement = getSpecialRequirement({ context });

  return selectedPickupPoint || selectedLangGuide || specialRequirement;
};

export const getAdditionalDetailsErrors = ({
  context,
}: ActivityStateWithoutValue) =>
  context[ParentState.activityAdditionalDetails].errors;

export const getFilteredPickupLocations = ({
  context,
}: ActivityStateWithoutValue) => {
  const pickupLocations = getPickupLocations({ context });
  if (!pickupLocations?.locations) return pickupLocations;

  const perBookingQuestions =
    context.activityShop?.bookingQuestions?.perBooking || [];

  // Check if transfer mode questions exist
  const hasTransferArrivalMode = perBookingQuestions.some(
    (q) => q.id === BookingQuestionId.TransferArrivalMode
  );
  const hasTransferDepartureMode = perBookingQuestions.some(
    (q) => q.id === BookingQuestionId.TransferDepartureMode
  );

  // If either transfer mode question exists, filter out AIRPORT and PORT locations
  if (hasTransferArrivalMode || hasTransferDepartureMode) {
    return {
      ...pickupLocations,
      locations: pickupLocations.locations.filter(
        (location) =>
          location.locationType !== PickupLocationType.Airport &&
          location.locationType !== PickupLocationType.Port
      ),
    };
  }

  return pickupLocations;
};
